import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '@/components/feature/Logo';
import { supabase } from '@/lib/supabase';

// Tạo email ẩn từ tên đăng nhập để tương thích với Supabase Auth (vốn bắt buộc có email)
const toAuthEmail = (username: string) =>
  `${username.trim().toLowerCase().replace(/[^a-z0-9._-]/g, '')}@goldsbvvn.com`;

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [referral, setReferral] = useState('');
  const [referralError, setReferralError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const getNextPath = () => {
    const next = searchParams.get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) return next;
    return '/bang-dieu-khien';
  };

  useEffect(() => {
    // Nếu đã đăng nhập, chuyển về dashboard
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        navigate(getNextPath(), { replace: true });
      }
    });
  }, [navigate, searchParams]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setReferralError('');

    if (mode === 'register') {
      if (referral.trim().toUpperCase() !== 'GOLDFAMILY') {
        setReferralError('Mã giới thiệu không hợp lệ. Vui lòng nhập đúng mã giới thiệu.');
        return;
      }
      if (!/^[0-9]{10,11}$/.test(phone.trim())) {
        setError('Số điện thoại không hợp lệ. Vui lòng nhập 10 - 11 chữ số.');
        return;
      }
    }

    const email = toAuthEmail(username);

    setLoading(true);
    try {
      if (mode === 'login') {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        navigate(getNextPath(), { replace: true });
      } else {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/dang-nhap`,
            data: {
              username: username.trim(),
              full_name: fullName.trim() || username.trim(),
              phone: phone.trim(),
              referral_code: referral.trim().toUpperCase(),
            },
          },
        });
        if (signUpError) throw signUpError;

        if (data.session) {
          navigate(getNextPath(), { replace: true });
        } else {
          setInfo(
            'Tài khoản của bạn đã được tạo. Vui lòng đăng nhập bằng tên đăng nhập và mật khẩu vừa đăng ký.'
          );
          setMode('login');
          setPassword('');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đã xảy ra lỗi. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>

        <div className="rounded-2xl border border-background-200/60 bg-background-100 p-6 md:p-8">
          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 text-center">
            {mode === 'login' ? 'Đăng nhập tài khoản' : 'Tạo tài khoản mới'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 text-center">
            {mode === 'login'
              ? 'Chào mừng bạn quay trở lại Goldsbvvn'
              : 'Bắt đầu đầu tư vàng cùng Goldsbvvn'}
          </p>

          <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-background-50 border border-background-200/60 mt-6 mb-6">
            <button onClick={() => { setMode('login'); setError(''); setInfo(''); }} className={`py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${mode === 'login' ? 'bg-primary-500 text-background-950' : 'text-foreground-600'}`}>
              Đăng nhập
            </button>
            <button onClick={() => { setMode('register'); setError(''); setInfo(''); }} className={`py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${mode === 'register' ? 'bg-primary-500 text-background-950' : 'text-foreground-600'}`}>
              Đăng ký
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-rose-400 text-base"></i>
              <p className="text-sm text-rose-400">{error}</p>
            </div>
          )}
          {info && (
            <div className="mb-4 rounded-lg bg-emerald-500/15 border border-emerald-500/30 p-3 flex items-center gap-2">
              <i className="ri-information-line text-emerald-400 text-base"></i>
              <p className="text-sm text-emerald-400">{info}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Họ và tên</label>
                <input
                  name="full_name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Nhập họ và tên"
                  autoComplete="name"
                  required
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Tên đăng nhập</label>
              <input
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Nhập tên đăng nhập"
                autoComplete="username"
                required
                className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500"
              />
            </div>
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Số điện thoại</label>
                <input
                  name="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="09xxxxxxxx"
                  inputMode="numeric"
                  required
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Mật khẩu</label>
              <input
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                required
                minLength={6}
                className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500"
              />
            </div>
            {mode === 'register' && (
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Mã giới thiệu</label>
                <input
                  name="referral_code"
                  value={referral}
                  onChange={(e) => { setReferral(e.target.value); setReferralError(''); }}
                  placeholder="Nhập mã giới thiệu"
                  required
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 uppercase"
                />
                {referralError && <p className="mt-1.5 text-xs text-rose-500">{referralError}</p>}
              </div>
            )}

            <button type="submit" disabled={loading} className="w-full py-3 rounded-md bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors gold-glow">
              {loading ? 'Vui lòng chờ...' : mode === 'login' ? 'Đăng nhập' : 'Đăng ký tài khoản'}
            </button>
          </form>

          <p className="mt-5 text-xs text-foreground-600 text-center leading-relaxed">
            Bằng việc tiếp tục, bạn đồng ý với <a href="#" className="text-primary-400 cursor-pointer">Điều khoản dịch vụ</a> và <a href="#" className="text-primary-400 cursor-pointer">Chính sách bảo mật</a> của chúng tôi.
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-foreground-600">
          <Link to="/" className="text-primary-400 hover:text-primary-300 cursor-pointer">← Quay lại trang chủ</Link>
        </p>
      </div>
    </div>
  );
}